// ==========================================
// Buddy API — the real backend for chat + threads
// ==========================================
// Thin HttpClient wrapper over Laravel's /api/v1/buddy/* endpoints. It maps the
// server payloads onto the existing BuddyThread / BuddyMessage UI models so the
// components and the thread/chat services stay unchanged — only the data source
// moves from mock to this.
// ==========================================

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { Accent, BuddyMessage, BuddyThread, Workflow } from '../models/buddy.types';

/** Thread shape as returned by BuddyChatController::presentThread(). */
interface ApiThread {
  id: string;
  title: string;
  titleIsCustom?: boolean;
  pinned?: boolean;
  archived?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

/** Message shape as returned by BuddyChatController::presentMessage(). */
interface ApiMessage {
  id: string | number;
  role: BuddyMessage['role'];
  text: string;
  createdAt?: number;
  metadata?: unknown;
}

export interface ChatResult {
  threadId: string;
  threadTitle: string;
  message: BuddyMessage;
}

/** One display point's live state, as reported by the engine. */
export interface ApiRunStep {
  label: string;
  state: 'pending' | 'active' | 'done' | 'failed';
  detail: string | null;
}

/** The pending approval payload (snake_case labels come straight from the engine). */
export interface ApiRunApproval {
  title: string;
  message: string;
  count: number | null;
  items: Array<Record<string, unknown>>;
  confirmLabel: string;
  cancelLabel: string;
}

/** A run's current state — BuddyWorkflowRunController::present(). Polled while running. */
export interface ApiRunState {
  id: string;
  workflowName: string;
  status: 'queued' | 'running' | 'awaiting_approval' | 'completed' | 'failed';
  stepLabels: string[];
  progress: ApiRunStep[];
  approval: ApiRunApproval | null;
  decision: 'approved' | 'declined' | null;
  summary: string | null;
  result: unknown;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class BuddyApiService {
  private readonly base = `${environment.apiBaseUrl}/api/v1/buddy`;

  constructor(private readonly http: HttpClient) {}

  /** GET /buddy/workflows — the palette workflows (system defaults + this office's own). */
  listWorkflows(): Observable<Workflow[]> {
    return this.http
      .get<any>(`${this.base}/workflows`)
      .pipe(map((res) => ((res?.data?.workflows ?? []) as any[]).map((w) => this.toWorkflow(w))));
  }

  /**
   * POST /buddy/workflows/{id}/run — START an async run. Laravel creates a run row, fires
   * it at the Python engine (which ACKs fast and works in the background), and returns the
   * run's initial state immediately. Poll pollRun() for progress.
   */
  startWorkflowRun(workflowId: string): Observable<ApiRunState> {
    return this.http
      .post<any>(`${this.base}/workflows/${workflowId}/run`, {})
      .pipe(map((res) => this.toRunState(res?.data?.run ?? {})));
  }

  /** GET /buddy/runs/{id} — poll a run's live state (progress, approval, outcome). */
  pollRun(runId: string): Observable<ApiRunState> {
    return this.http
      .get<any>(`${this.base}/runs/${runId}`)
      .pipe(map((res) => this.toRunState(res?.data?.run ?? {})));
  }

  /** POST /buddy/runs/{id}/decision — the user's Yes/No on a paused run; resumes the engine. */
  decideRun(runId: string, approved: boolean): Observable<ApiRunState> {
    return this.http
      .post<any>(`${this.base}/runs/${runId}/decision`, { approved })
      .pipe(map((res) => this.toRunState(res?.data?.run ?? {})));
  }

  /** Map the server run payload onto ApiRunState (normalising the approval labels). */
  private toRunState(r: any): ApiRunState {
    const approval: ApiRunApproval | null = r?.approval
      ? {
          title: r.approval.title ?? 'Continue?',
          message: r.approval.message ?? '',
          count: r.approval.count ?? null,
          items: Array.isArray(r.approval.items) ? r.approval.items : [],
          confirmLabel: r.approval.confirm_label ?? r.approval.confirmLabel ?? 'Yes',
          cancelLabel: r.approval.cancel_label ?? r.approval.cancelLabel ?? 'No',
        }
      : null;

    return {
      id: String(r?.id ?? ''),
      workflowName: r?.workflowName ?? '',
      status: r?.status ?? 'running',
      stepLabels: Array.isArray(r?.stepLabels) ? r.stepLabels : [],
      progress: Array.isArray(r?.progress) ? r.progress : [],
      approval,
      decision: r?.decision ?? null,
      summary: r?.summary ?? null,
      result: r?.result ?? null,
      error: r?.error ?? null,
    };
  }

  /** BuddyWorkflowController::present() → the frontend Workflow shape. */
  private toWorkflow(w: any): Workflow {
    return {
      id: String(w.id),
      name: w.name ?? '',
      glyph: w.glyph ?? '◆',
      accent: (w.accent ?? 'action') as Accent,
      description: w.description ?? '',
      stepLabels: Array.isArray(w.stepLabels) ? w.stepLabels : [],
      pinned: !!w.pinned,
      testMode: !!w.testMode,
      failsAtStep: w.failsAtStep ?? null,
    };
  }

  /** Sidebar list of the current user's threads (no messages). */
  listThreads(): Observable<BuddyThread[]> {
    return this.http
      .get<{ data: ApiThread[] }>(`${this.base}/threads`)
      .pipe(map((res) => (res?.data ?? []).map((t) => this.toThread(t))));
  }

  /** A thread's messages, for rendering the chat when it is opened. */
  getMessages(uuid: string): Observable<BuddyMessage[]> {
    return this.http
      .get<{ data: { messages: ApiMessage[] } }>(`${this.base}/threads/${uuid}/messages`)
      .pipe(map((res) => (res?.data?.messages ?? []).map((m) => this.toMessage(m))));
  }

  /** Send a message. thread_id = null means "new chat" → the server creates it. */
  chat(payload: { thread_id: string | null; prompt: string }): Observable<ChatResult> {
    return this.http.post<any>(`${this.base}/chat`, payload).pipe(
      map((res) => ({
        threadId: res?.thread_id ?? '',
        threadTitle: res?.thread_title ?? '',
        message: this.toMessage(res?.message ?? {}),
      }))
    );
  }

  updateThread(
    uuid: string,
    patch: { title?: string; pinned?: boolean; archived?: boolean }
  ): Observable<unknown> {
    return this.http.patch(`${this.base}/threads/${uuid}`, patch);
  }

  deleteThread(uuid: string): Observable<unknown> {
    return this.http.delete(`${this.base}/threads/${uuid}`);
  }

  // ------------------------------------------
  // Mapping API payloads → UI models
  // ------------------------------------------

  private toThread(t: ApiThread): BuddyThread {
    const created = t.createdAt ?? Date.now();
    return {
      id: t.id,
      title: t.title ?? 'New chat',
      titleIsCustom: !!t.titleIsCustom,
      messages: [], // loaded lazily via getMessages()
      runs: {},
      createdAt: created,
      updatedAt: t.updatedAt ?? created,
      pinned: !!t.pinned,
      archived: !!t.archived,
    };
  }

  private toMessage(m: Partial<ApiMessage>): BuddyMessage {
    return {
      id: String(m.id ?? ''),
      role: (m.role ?? 'assistant') as BuddyMessage['role'],
      text: m.text ?? '',
      createdAt: m.createdAt ?? Date.now(),
      streaming: false,
      runId: null,
      reward: null,
      actions: [],
      variant: 'default',
      collapsed: false,
    };
  }
}
